import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { t } from "ttag";

import type { Form as FormType } from "@/features/forms/models";
import { evaluateFilterCondition } from "@/features/forms/utils";
import { FIELD_TYPE } from "@/shared/models/legacy-constants";
import { Form } from "@/shared/ui";
import { Field, ReadOnlyField } from "./components";
import {
  isHiddenFieldByLabel,
  isInvalidFieldType,
  isReadOnlyType,
} from "./utils";

interface HealthieFormFieldProps {
  healthieField: FormType["fields"][number];
}

export const HealthieFormField = ({
  healthieField,
}: HealthieFormFieldProps) => {
  const fieldCondition = healthieField.customModuleCondition;
  const fieldConditionName = fieldCondition?.conditionalCustomModuleId ?? "";

  const { control, resetField } = useFormContext();
  const conditionFieldValue = useWatch({
    control,
    defaultValue: "",
    name: fieldConditionName,
  }) as string;

  const showConditionedField =
    !!fieldCondition &&
    evaluateFilterCondition(
      fieldCondition.filterType,
      conditionFieldValue,
      fieldCondition.valueToFilter,
    );

  useEffect(() => {
    if (!showConditionedField) {
      resetField(healthieField.id);
    }
  }, [healthieField.id, resetField, showConditionedField]);

  const showField = !fieldCondition || showConditionedField;
  if (!showField) {
    return null;
  }

  if (
    (isReadOnlyType(healthieField.type) &&
      isHiddenFieldByLabel(healthieField.label)) ||
    isInvalidFieldType(healthieField.type) ||
    healthieField.type === FIELD_TYPE.HIDDEN
  ) {
    return null;
  }

  const defaultValue = healthieField.type === FIELD_TYPE.FILE ? undefined : "";
  const label = healthieField.label
    ? `${healthieField.label}${healthieField.required ? "*" : ` (${t`optional`})`}`
    : "";

  return (
    <fieldset className="flex flex-col gap-3.5 rounded-2xl bg-secondary-50 p-3.5 text-secondary-950">
      {isReadOnlyType(healthieField.type) ? (
        <ReadOnlyField healthieField={healthieField} />
      ) : (
        <Form.Field
          control={control}
          defaultValue={healthieField.value ?? defaultValue}
          name={healthieField.id}
          rules={{
            required: healthieField.required && t`This field is required`,
          }}
          render={(renderProps) => (
            <Form.Item>
              <Form.Label>{label}</Form.Label>
              <Form.Control>
                <Field healthieField={healthieField} {...renderProps} />
              </Form.Control>
              <Form.Message />
            </Form.Item>
          )}
        />
      )}
    </fieldset>
  );
};
